<template>
  <header
    class="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    :class="{
      'bg-white/90 backdrop-blur-md shadow-sm': scrolled,
      'bg-transparent': !scrolled
    }"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16 md:h-20">
        <router-link to="/" class="text-xl md:text-2xl font-bold text-dark tracking-tight">
          SHOP
        </router-link>

        <nav class="hidden md:flex items-center space-x-8">
          <router-link
            v-for="item in navItems"
            :key="item.path"
            :to="item.path"
            class="text-sm font-medium text-secondary hover:text-primary transition-colors relative group"
            active-class="text-primary"
          >
            {{ $t(item.label) }}
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"
            ></span>
          </router-link>
        </nav>

        <div class="flex items-center gap-4">
          <div class="relative">
            <button
              @click="toggleLangMenu"
              class="flex items-center gap-1 text-sm font-medium text-secondary hover:text-primary transition-colors"
            >
              <span>{{ currentLangLabel }}</span>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              v-if="langMenuOpen"
              class="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
            >
              <button
                v-for="lang in languages"
                :key="lang.value"
                @click="changeLanguage(lang.value)"
                class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                :class="locale === lang.value ? 'text-primary font-medium' : 'text-secondary'"
              >
                {{ lang.label }}
              </button>
            </div>
          </div>

          <button
            @click="store.toggleMobileMenu"
            class="md:hidden p-2 text-secondary hover:text-primary transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="store.isMobileMenuOpen"
      class="md:hidden bg-white border-t border-gray-100 shadow-lg"
    >
      <div class="px-4 py-3 space-y-1">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          @click="store.isMobileMenuOpen = false"
          class="block px-3 py-2 rounded-md text-base font-medium text-secondary hover:text-primary hover:bg-gray-50 transition-colors"
          active-class="text-primary bg-gray-50"
        >
          {{ $t(item.label) }}
        </router-link>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores'

const { locale } = useI18n()
const store = useAppStore()

const scrolled = ref(false)
const langMenuOpen = ref(false)

const navItems = [
  { path: '/', label: 'nav.products' },
  { path: '/hot', label: 'nav.home' },
  { path: '/contact', label: 'nav.contact' }
]

const languages = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' }
]

const currentLangLabel = computed(() => {
  const lang = languages.find((l) => l.value === locale.value)
  return lang?.label || '简体中文'
})

function toggleLangMenu() {
  langMenuOpen.value = !langMenuOpen.value
}

function changeLanguage(lang: string) {
  locale.value = lang
  store.setLocale(lang)
  langMenuOpen.value = false
}

function handleScroll() {
  scrolled.value = window.scrollY > 20
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (!target.closest('.relative')) {
    langMenuOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleClickOutside)
})
</script>
